/**
 * Strict integer guards.
 *
 * Every `number` that represents a whole quantity (a level, an index, a basis
 * point value, a count of seconds) passes through here before it is used.
 *
 * The rule is **reject, never coerce**. `Math.trunc` and `Math.floor` silently
 * turn `1.9` into `1` and `NaN` into `NaN`; in a server-authoritative economy
 * that converts a malformed request into a plausible-looking wrong result
 * instead of an error. Anything that can legitimately exceed
 * `Number.MAX_SAFE_INTEGER` must be a `bigint`, not a coerced `number`.
 */

export function assertSafeInteger(value: number, label = 'value'): number {
  if (typeof value !== 'number') {
    throw new TypeError(`${label} must be a number, received ${typeof value}`);
  }
  if (Number.isNaN(value)) {
    throw new RangeError(`${label} must not be NaN`);
  }
  if (!Number.isFinite(value)) {
    throw new RangeError(`${label} must be finite, received ${value}`);
  }
  if (!Number.isInteger(value)) {
    throw new RangeError(`${label} must be an integer, received ${value}`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(
      `${label} exceeds Number.MAX_SAFE_INTEGER (${value}); use bigint for values this large`,
    );
  }
  return value;
}

export function assertSafeIntegerInRange(
  value: number,
  min: number,
  max: number,
  label = 'value',
): number {
  assertSafeInteger(value, label);
  if (value < min || value > max) {
    throw new RangeError(`${label} must be within [${min}, ${max}], received ${value}`);
  }
  return value;
}

export function assertNonNegativeSafeInteger(value: number, label = 'value'): number {
  assertSafeInteger(value, label);
  if (value < 0) {
    throw new RangeError(`${label} must not be negative, received ${value}`);
  }
  return value;
}

export function isSafeIntegerInRange(value: number, min: number, max: number): boolean {
  return Number.isSafeInteger(value) && value >= min && value <= max;
}

/**
 * Converts to `bigint` without ever truncating.
 *
 * `BigInt(1.5)` throws in the runtime, but only after the value has already
 * travelled through the call stack; doing the check here keeps the error
 * message attributable to the caller's parameter.
 */
export function toExactBigInt(value: number | bigint, label = 'value'): bigint {
  if (typeof value === 'bigint') {
    return value;
  }
  return BigInt(assertSafeInteger(value, label));
}
